<script setup lang="ts">
  import { defaultFlows } from '@/configs/default-flows'
  import { useFlowConfigStore } from '@/store/flow-config.store'
  import {
    FormFieldContainer,
    FormInput,
    SwitchButton,
    UiButton,
    TabContainer,
    type FlowConfig,
    type Tab,
    useToastStore,
  } from '@logistiek-zonder-papier/lzp-library'
  import { computed, ref, watch } from 'vue'
  import { useRouter } from 'vue-router'
  import LogicalOperatorSelect from '@/components/form-wizard/LogicalOperatorSelect.vue'
  import Predicates from '@/components/form-wizard/Predicates.vue'
  import { debounce } from '@/utils/debounce'
  import { isAxiosError } from 'axios'
  import type { UUID } from 'node:crypto'
  import DuplicateFlowModal from '@/components/form-wizard/DuplicateFlowModal.vue'
  import DynamicVariableInput from '@/components/form/DynamicVariableInput.vue'

  const showCopyFlowModal = ref<boolean>(false)

  const router = useRouter()

  const tabs: Tab[] = [
    { key: 'general', label: 'general' },
    { key: 'conditions', label: 'conditions' },
    { key: 'activityIndicator', label: 'activity indicator' },
  ]
  const activeTab = ref<string>('general')

  const model = defineModel<FlowConfig>({ required: true })
  const isDefaultFlow = computed(() => defaultFlows.some(flow => flow.type === model.value.type))

  const flowConfigStore = useFlowConfigStore()
  const toastStore = useToastStore()

  async function submitUpdate() {
    try {
      await flowConfigStore.updateFlow(model.value)
      toastStore.success('Configuratie succesvol bijgewerkt')
    } catch (error) {
      toastStore.error(
        'Er is een fout opgetreden bij het bijwerken van de configuratie. Probeer het opnieuw.'
      )
      throw error
    }
  }

  async function deleteFlow() {
    await flowConfigStore.deleteFlow(model.value)

    router.back()
  }

  async function exportFlow() {
    const totalFlow = await flowConfigStore.getTotalFlow(model.value)

    try {
      await navigator.clipboard.writeText(JSON.stringify(totalFlow.data))

      toastStore.success('Flow gekopieerd naar klembord')
    } catch (err) {
      console.error('Failed to copy:', err)
      toastStore.error('Could not get total flow.')
    }
  }

  async function copyFlowToSubApp(subAppId: UUID) {
    try {
      await flowConfigStore.copyFlowToSubApp(model.value, subAppId)

      toastStore.success('Flow is gekopieerd')
      showCopyFlowModal.value = false
    } catch (error) {
      if (isAxiosError(error)) {
        console.log(error)
        toastStore.error('Could not copy sub app')
      } else {
        throw error
      }
    }
  }

  watch(
    () => model.value.predicates,
    () => updateFlowConfig(),
    { deep: true }
  )

  function setActivityIndicatorPath(value: string) {
    model.value.activityIndicatorPath = value.trim() || null
    updateFlowConfig()
  }

  const updateFlowConfig = debounce(async () => {
    await submitUpdate()
  }, 500)
</script>

<template>
  <TabContainer v-model="activeTab" :tabs="tabs">
    <template #general>
      <FormInput label="Label" v-model="model.label" @input="updateFlowConfig()" />
      <FormFieldContainer label="Description">
        <InputField
          v-model="model.description"
          type="text"
          textarea
          @update:modelValue="updateFlowConfig()"
        />
      </FormFieldContainer>
      <FormFieldContainer label="Verborgen">
        <SwitchButton size="sm" v-model="model.hidden" @update:modelValue="updateFlowConfig()" />
      </FormFieldContainer>
      <FormFieldContainer label="Registratie Vereist">
        <SwitchButton
          size="sm"
          v-model="model.requiresRegistration"
          @update:modelValue="updateFlowConfig()"
        />
      </FormFieldContainer>

      <div>
        <UiButton label="Export flow" @click="exportFlow" class="fullwidth" />
      </div>

      <div>
        <UiButton
          label="Duplicate to sub app"
          @click="showCopyFlowModal = true"
          class="fullwidth"
        />
      </div>

      <div>
        <UiButton
          v-if="isDefaultFlow"
          variant="negative"
          label="Flow Resetten"
          @click="deleteFlow"
          class="fullwidth"
        />
        <UiButton
          v-else
          variant="negative"
          label="Flow Verwijderen"
          @click="deleteFlow"
          class="fullwidth"
        />
      </div>
    </template>

    <template #conditions>
      <Predicates v-model="model.predicates" />

      <LogicalOperatorSelect
        v-model="model.predicateCombinator"
        label="Filter Logic"
        nullable
        @update:modelValue="updateFlowConfig()"
      />
    </template>

    <template #activityIndicator>
      <DynamicVariableInput
        label="Activity indicator value"
        placeholder="globalData.shipments.length"
        hint="Path to a number shown in a bubble on this flow's button. Nothing is shown when it isn't a number or is 0."
        :modelValue="model.activityIndicatorPath ?? ''"
        @update:modelValue="setActivityIndicatorPath"
        type="text"
        :optional="true"
        :validate-paths="true"
      />
      <p class="tab-hint">The indicator colours are configured under Style → Flow Style.</p>
    </template>
  </TabContainer>

  <DuplicateFlowModal
    v-model:show="showCopyFlowModal"
    @select="subAppId => copyFlowToSubApp(subAppId)"
  />
</template>

<style scoped lang="css">
  .fullwidth {
    width: 100%;
  }

  .tab-hint {
    color: var(--colours-grey-4);
    font-size: 0.875rem;
  }
</style>
